import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { isAcceptablePassword } from '@il-colpo/shared/account';
import { getAccountStore } from '../../account/accountStore.ts';
import {
  PROVIDERS,
  type AccountClient,
  type AccountUser,
  type TotpEnrollment,
} from '../../account/types.ts';
import { useToastStore } from '../../state/toastStore.ts';
import { Button, Panel, ScreenFrame, TextField } from '../../ui/index.ts';
import styles from './Account.module.css';
import {
  FormError,
  FormNote,
  Page,
  useAccountSession,
  useAction,
  useAuthMethods,
} from './common.tsx';

const digits = (value: string): string => value.replace(/\D/g, '').slice(0, 6);

function Methods({ user }: { user: AccountUser }) {
  const { t } = useTranslation('account');
  return (
    <Panel title={t('security.methods')}>
      <ul className={styles['stack']} style={{ margin: 0, paddingLeft: '1.2rem' }}>
        {user.anonymous ? <li>{t('security.methodGuest')}</li> : null}
        {!user.anonymous && user.email ? (
          <li>{t('security.methodEmail', { email: user.email })}</li>
        ) : null}
        {user.providers
          .filter((p) => p !== 'email')
          .map((p) => (
            <li key={p}>{t('security.methodProvider', { provider: p })}</li>
          ))}
      </ul>
    </Panel>
  );
}

/** A guest becomes an account: an e-mail and a password, confirmed with a code. */
function AddEmail({ client }: { client: AccountClient }) {
  const { t } = useTranslation('account');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'form' | 'code'>('form');
  const [code, setCode] = useState('');
  const [resent, setResent] = useState(false);
  const { pending, error, run } = useAction();

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const r = await run(() => client.linkEmail(email.trim(), password));
    if (r.ok) setStep('code');
  };
  const verify = async (e: FormEvent) => {
    e.preventDefault();
    const r = await run(() => client.verifyCode(email.trim(), code, 'link'));
    if (r.ok) useToastStore.getState().push(t('security.addEmailDone'), 'success');
  };

  return (
    <Panel title={t('security.addEmailTitle')}>
      {step === 'form' ? (
        <form className={styles['form']} onSubmit={(e) => void send(e)} noValidate>
          <p className={styles['note']}>{t('security.addEmailBody')}</p>
          <TextField
            label={t('auth.email')}
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={setEmail}
          />
          <TextField
            label={t('auth.password')}
            type="password"
            autoComplete="new-password"
            hint={t('auth.passwordHint')}
            value={password}
            onChange={setPassword}
          />
          <FormError code={error} />
          <Button
            type="submit"
            loading={pending}
            disabled={!email || !isAcceptablePassword(password)}
          >
            {t('security.addEmailSend')}
          </Button>
        </form>
      ) : (
        <form className={styles['form']} onSubmit={(e) => void verify(e)} noValidate>
          <p className={styles['note']}>{t('auth.codeSent', { email: email.trim() })}</p>
          <TextField
            label={t('auth.code')}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(v) => {
              setCode(digits(v));
            }}
          />
          <FormError code={error} />
          {resent ? <FormNote>{t('auth.resent')}</FormNote> : null}
          <div className={styles['row']}>
            <Button type="submit" loading={pending} disabled={code.length !== 6}>
              {t('auth.verify')}
            </Button>
            <button
              type="button"
              className={styles['linkButton']}
              onClick={() => {
                setResent(false);
                void run(() => client.resendCode(email.trim(), 'link')).then((r) => {
                  if (r.ok) setResent(true);
                });
              }}
            >
              {t('auth.resend')}
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}

function LinkProviders({ client, user }: { client: AccountClient; user: AccountUser }) {
  const { t } = useTranslation('account');
  const { pending, error, run } = useAction();
  const methods = useAuthMethods();
  const free = PROVIDERS.filter(
    (p) => methods.providers.includes(p) && !user.providers.includes(p),
  );
  if (free.length === 0) return null;
  return (
    <Panel title={t('security.linkTitle')}>
      <div className={styles['stack']}>
        <div className={styles['providers']}>
          {free.map((p) => (
            <Button
              key={p}
              variant="secondary"
              loading={pending}
              onClick={() => {
                void run(() => client.linkProvider(p));
              }}
            >
              {t('security.linkProvider', { provider: t(`auth.provider.${p}`) })}
            </Button>
          ))}
        </div>
        <p className={styles['note']}>{t('security.linkHint')}</p>
        <FormError code={error} />
      </div>
    </Panel>
  );
}

function ChangePassword({ client }: { client: AccountClient }) {
  const { t } = useTranslation('account');
  const [password, setPassword] = useState('');
  const [saved, setSaved] = useState(false);
  const { pending, error, run } = useAction();
  return (
    <Panel title={t('security.passwordTitle')}>
      <form
        className={styles['form']}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setSaved(false);
          void run(() => client.setPassword(password)).then((r) => {
            if (r.ok) {
              setSaved(true);
              setPassword('');
            }
          });
        }}
      >
        <TextField
          label={t('auth.newPassword')}
          type="password"
          autoComplete="new-password"
          hint={t('auth.passwordHint')}
          value={password}
          onChange={setPassword}
        />
        <FormError code={error} />
        {saved ? <FormNote>{t('auth.passwordSaved')}</FormNote> : null}
        <div>
          <Button type="submit" loading={pending} disabled={!isAcceptablePassword(password)}>
            {t('auth.setPassword')}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

/** The second step at sign-in: an authenticator app. */
function Authenticator({
  client,
  user,
  mode,
}: {
  client: AccountClient;
  user: AccountUser;
  mode: string;
}) {
  const { t } = useTranslation('account');
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState('');
  const { pending, error, run } = useAction();
  const factor = user.totpFactors[0];

  if (factor) {
    return (
      <Panel title={t('security.authenticatorTitle')}>
        <div className={styles['stack']}>
          <p>{t('security.authenticatorOn')}</p>
          <FormError code={error} />
          <div>
            <Button
              variant="danger"
              loading={pending}
              onClick={() => {
                void run(() => client.removeTotp(factor.id)).then((r) => {
                  if (r.ok) {
                    useToastStore.getState().push(t('security.authenticatorRemoved'), 'info');
                  }
                });
              }}
            >
              {t('security.authenticatorRemove')}
            </Button>
          </div>
        </div>
      </Panel>
    );
  }

  if (!enrollment) {
    return (
      <Panel title={t('security.authenticatorTitle')}>
        <div className={styles['stack']}>
          <p className={styles['note']}>{t('security.authenticatorOff')}</p>
          <FormError code={error} />
          <div>
            <Button
              loading={pending}
              onClick={() => {
                void run(() => client.enrollTotp()).then((r) => {
                  if (r.ok) setEnrollment(r.value);
                });
              }}
            >
              {t('security.authenticatorSetup')}
            </Button>
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel title={t('security.authenticatorTitle')}>
      <form
        className={styles['form']}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void run(() => client.confirmTotp(enrollment.factorId, code)).then((r) => {
            if (r.ok) {
              setEnrollment(null);
              setCode('');
              useToastStore.getState().push(t('security.authenticatorEnabled'), 'success');
            }
          });
        }}
      >
        <p className={styles['note']}>{t('security.authenticatorScan')}</p>
        {enrollment.qrImage ? (
          <img
            className={styles['qr']}
            src={enrollment.qrImage}
            alt={t('security.authenticatorQrAlt')}
          />
        ) : null}
        {mode === 'demo' ? (
          <p className={styles['note']}>{t('security.authenticatorQrNote')}</p>
        ) : null}
        <div className={styles['stack']}>
          <span className={styles['note']}>{t('security.authenticatorKey')}</span>
          <code className={styles['secret']}>{enrollment.secret}</code>
        </div>
        <TextField
          label={t('auth.code')}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(v) => {
            setCode(digits(v));
          }}
        />
        <FormError code={error} />
        <div>
          <Button type="submit" loading={pending} disabled={code.length !== 6}>
            {t('security.authenticatorConfirm')}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

export function SecurityScreen() {
  const methods = useAuthMethods();
  const { t } = useTranslation('account');
  const { available, mode, load, snapshot, client } = useAccountSession();
  const { pending, run } = useAction();

  if (!available || (load === 'ready' && snapshot.status === 'signedOut')) {
    return <Navigate to="/account" replace />;
  }
  const user = snapshot.user;
  return (
    <ScreenFrame title={t('security.title')} backTo="/account">
      <Page>
        {client && user ? (
          <>
            <Methods user={user} />
            {user.anonymous && methods.code ? <AddEmail client={client} /> : null}
            <LinkProviders client={client} user={user} />
            {!user.anonymous && user.providers.includes('email') ? (
              <ChangePassword client={client} />
            ) : null}
            {!user.anonymous ? <Authenticator client={client} user={user} mode={mode} /> : null}
            <div className={styles['rowEnd']}>
              <Button
                variant="ghost"
                loading={pending}
                onClick={() => {
                  void run(() =>
                    getAccountStore().getState().signOut({ everywhere: true, wipeDevice: false }),
                  );
                }}
              >
                {t('security.signOutEverywhere')}
              </Button>
            </div>
          </>
        ) : (
          <Panel>
            <p className={styles['note']} role="status">
              {t('loading')}
            </p>
          </Panel>
        )}
      </Page>
    </ScreenFrame>
  );
}
